import type { QueryClient } from '@tanstack/react-query';
import {
  getGetExpoQueryKey,
  getGetStationTasksQueryKey,
  type StationTaskStatus,
  type StationView,
} from '@ordr-as/ordr-service-types/epos';

const INVALIDATE_DEBOUNCE_MS = 300;
// Even under a continuous burst, never let the board go stale longer than this.
const INVALIDATE_MAX_WAIT_MS = 1_000;

// `kds.tasks.added` and `kds.task.updated` share this shape.
type TaskEventPayload = {
  stationId: string;
  tasks: {
    taskId: string;
    stationId: string;
    eventType: string;
    payload: {
      bookingOrderId: number;
      groupId: string;
      status: string;
    };
  }[];
};

/**
 * Interprets KDS websocket messages and syncs them into the react-query caches (the per-station
 * board read models and the expo read model). This is the only place that knows both the wire
 * payloads and the cache layout; the websocket client stays a plain transport.
 */
export function createKdsTasksCacheSync(queryClient: QueryClient, locationId: number) {
  const expoQueryKey = getGetExpoQueryKey(locationId);
  // Only one station board is active at a time, but a change anywhere (incl. an upstream station
  // finishing, which moves a viewed task's "waiting for") must refresh it — so invalidate every
  // station-tasks query for the location and let the active one refetch.
  const stationTasksPrefix = `/v1/kds/locations/${locationId}/stations/`;
  let debounceTimer: number | undefined;
  let maxWaitTimer: number | undefined;

  const flush = () => {
    clearTimeout(debounceTimer);
    clearTimeout(maxWaitTimer);
    debounceTimer = undefined;
    maxWaitTimer = undefined;
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(stationTasksPrefix),
    });
    queryClient.invalidateQueries({ queryKey: expoQueryKey });
  };

  const scheduleInvalidate = () => {
    // Debounced so a burst collapses to one request, but maxWait guarantees a
    // flush during a sustained stream that would otherwise keep resetting the timer.
    clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(flush, INVALIDATE_DEBOUNCE_MS);
    if (maxWaitTimer === undefined) {
      maxWaitTimer = window.setTimeout(flush, INVALIDATE_MAX_WAIT_MS);
    }
  };

  const onSubscribed = () => {
    // GET is the source of truth for current state; refetch at the (re)subscribe
    // sync point and let the socket only drive updates after.
    scheduleInvalidate();
  };

  const onTaskEvent = (payload: TaskEventPayload) => {
    // The push payload has no items, so only patch statuses of tasks we already know in each
    // station's cache (never insert skeletons), grouped by the task's station, then refetch truth.
    const statusByTaskIdByStation = new Map<number, Map<number, string>>();
    for (const task of payload.tasks) {
      const stationId = Number(task.stationId);
      const statusByTaskId = statusByTaskIdByStation.get(stationId) ?? new Map<number, string>();
      statusByTaskId.set(Number(task.taskId), task.payload.status);
      statusByTaskIdByStation.set(stationId, statusByTaskId);
    }

    for (const [stationId, statusByTaskId] of statusByTaskIdByStation) {
      queryClient.setQueryData<StationView>(
        getGetStationTasksQueryKey(locationId, stationId),
        (current) =>
          current && {
            ...current,
            tasks: current.tasks.map((task) => {
              const status = statusByTaskId.get(task.id);
              return status ? { ...task, status: status as StationTaskStatus } : task;
            }),
          }
      );
    }

    scheduleInvalidate();
  };

  return {
    onMessage: (type: string, data: unknown) => {
      if (type === 'subscribe.v1.kds.ok') onSubscribed();
      if (type === 'kds.tasks.added') onTaskEvent(data as TaskEventPayload);
      if (type === 'kds.task.updated') onTaskEvent(data as TaskEventPayload);
    },
    dispose: () => {
      clearTimeout(debounceTimer);
      clearTimeout(maxWaitTimer);
    },
  };
}
